// frontend/src/components/Modals/AutomationHistoryModal/AutomationHistoryModal.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../../config';
import './AutomationHistoryModal.css';

const AutomationHistoryModal = ({ isOpen, onClose, automationId, automationName, automationData }) => {
    const [historyData, setHistoryData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('info'); // 'info' veya 'logs'
    const [activityLogs, setActivityLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    useEffect(() => {
        if (isOpen && automationData) {
            setHistoryData({
                automation_info: {
                    id: automationData.id,
                    name: automationData.name,
                    created_by: automationData.created_by_username || automationData.created_by || 'Bilinmiyor',
                    created_at: automationData.created_at,
                    updated_by: automationData.updated_by_username || automationData.updated_by || automationData.created_by_username || automationData.created_by || 'Bilinmiyor',
                    updated_at: automationData.updated_at || automationData.created_at,
                    last_run: automationData.last_run,
                    is_active: automationData.is_active,
                    type: automationData.type,
                    schedule_type: automationData.schedule_type,
                    schedule_value: automationData.schedule_value,
                    selected_tags: automationData.selected_tags,
                    selected_channels: automationData.selected_channels
                }
            });
        }
    }, [isOpen, automationData]);

    // Modal açıldığında state'leri sıfırla
    useEffect(() => {
        if (isOpen) {
            setActiveTab('info');
        }
    }, [isOpen]);

    // İşlem geçmişini yükle
    useEffect(() => {
        if (isOpen && automationId && activeTab === 'logs') {
            fetchActivityLogs();
        }
    }, [isOpen, automationId, activeTab]);

    const fetchActivityLogs = async () => {
        setLogsLoading(true);
        setActivityLogs([]);
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_URL}/automation/rules/${automationId}/logs`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            if (response.data.success) {
                const logs = response.data.logs || [];
                setActivityLogs(logs);
            } else {
                console.error('Log yükleme başarısız:', response.data.error);
                setActivityLogs([]);
            }
        } catch (error) {
            console.error('İşlem geçmişi yüklenemedi:', error);
            setActivityLogs([]);
        } finally {
            setLogsLoading(false);
        }
    };

    const formatDate = (dateString, alwaysShowFullDate = false) => {
        if (!dateString) return 'Henüz yok';
        
        try {
            let date;
            
            if (dateString.includes('Z') || dateString.includes('+')) {
                date = new Date(dateString);
            } else {
                date = new Date(dateString.replace(' ', 'T'));
            }
            
            if (isNaN(date.getTime())) {
                console.error('Geçersiz tarih:', dateString);
                return 'Geçersiz tarih';
            }
            
            const options = {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                weekday: 'long'
            };
            
            const fullDate = date.toLocaleDateString('tr-TR', options);
            
            if (alwaysShowFullDate) {
                return fullDate;
            }
            
            const now = new Date();
            const diffTime = now - date;
            const diffSeconds = Math.floor(Math.abs(diffTime) / 1000);
            const diffMinutes = Math.floor(Math.abs(diffTime) / (1000 * 60));
            const diffHours = Math.floor(Math.abs(diffTime) / (1000 * 60 * 60));
            const diffDays = Math.floor(Math.abs(diffTime) / (1000 * 60 * 60 * 24));
            
            let relativeTime = '';
            
            if (diffTime < -60000) {
                return fullDate + ' (Gelecek tarih?)';
            }
            
            if (diffSeconds < 60) {
                relativeTime = diffSeconds < 5 ? 'Şimdi' : `${diffSeconds} saniye önce`;
            } else if (diffMinutes < 60) {
                relativeTime = diffMinutes === 1 ? '1 dakika önce' : `${diffMinutes} dakika önce`;
            } else if (diffHours < 24) {
                relativeTime = diffHours === 1 ? '1 saat önce' : `${diffHours} saat önce`;
            } else if (diffDays === 1) {
                relativeTime = 'Dün';
            } else if (diffDays < 7) {
                relativeTime = `${diffDays} gün önce`;
            } else if (diffDays < 30) {
                const weeks = Math.floor(diffDays / 7);
                relativeTime = weeks === 1 ? '1 hafta önce' : `${weeks} hafta önce`;
            } else if (diffDays < 365) {
                const months = Math.floor(diffDays / 30);
                relativeTime = months === 1 ? '1 ay önce' : `${months} ay önce`;
            } else {
                const years = Math.floor(diffDays / 365);
                relativeTime = years === 1 ? '1 yıl önce' : `${years} yıl önce`;
            }
            
            const timeOnly = date.toLocaleTimeString('tr-TR', { 
                hour: '2-digit', 
                minute: '2-digit'
            });
            
            const dateOnly = date.toLocaleDateString('tr-TR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
            
            const today = new Date();
            const isToday = date.getDate() === today.getDate() && 
                           date.getMonth() === today.getMonth() && 
                           date.getFullYear() === today.getFullYear();
            
            if (isToday) {
                return `Bugün ${timeOnly} (${relativeTime})`;
            } else if (diffDays === 1) {
                return `Dün ${timeOnly}`;
            } else if (diffDays < 7) {
                return `${dateOnly} ${timeOnly} (${relativeTime})`;
            } else {
                return `${dateOnly} ${timeOnly}`;
            }
            
        } catch (error) {
            console.error('Tarih formatlama hatası:', error, 'Tarih:', dateString);
            return 'Hatalı tarih';
        }
    };

    const getLogIcon = (log) => {
        // log objesinde icon ve color varsa onları kullan
        if (log.icon && log.color) {
            return { icon: log.icon, color: log.color };
        }
        
        // Yoksa type'a göre belirle
        const icons = {
            'success': { icon: '✅', color: '#10b981' },
            'error': { icon: '❌', color: '#ef4444' },
            'warning': { icon: '⚠️', color: '#f59e0b' },
            'info': { icon: 'ℹ️', color: '#3b82f6' },
            'scheduled': { icon: '⏰', color: '#3b82f6' },
            'sent': { icon: '📤', color: '#10b981' },
            'failed': { icon: '❌', color: '#ef4444' }
        };
        
        return icons[log.type] || { icon: '📋', color: '#6b7280' };
    };

    if (!isOpen) return null;

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="history-modal-container" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>📊 Otomasyon Bilgileri</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Bilgiler yükleniyor...</p>
                    </div>
                ) : historyData ? (
                    <>
                        <div className="modal-subheader">
                            <h3>{automationName || 'Otomasyon'}</h3>
                        </div>

                        <div className="tabs">
                            <button 
                                className={`tab ${activeTab === 'info' ? 'active' : ''}`}
                                onClick={() => setActiveTab('info')}
                                type="button"
                            >
                                📋 Genel Bilgiler
                            </button>
                            <button 
                                className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
                                onClick={() => setActiveTab('logs')}
                                type="button"
                            >
                                📜 İşlem Geçmişi
                            </button>
                        </div>

                        <div className="modal-body">
                            {activeTab === 'info' ? (
                                <div className="info-content">
                                    <div className="info-section">
                                        <h4>🤖 Otomasyon Bilgileri</h4>
                                        <div className="info-grid">
                                            <div className="info-item">
                                                <span className="label">Otomasyon Adı:</span>
                                                <span className="value">{historyData.automation_info?.name}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="label">Tür:</span>
                                                <span className="value">
                                                    {historyData.automation_info?.type === 'tag-based' ? '🏷️ Etiket Bazlı' : '📅 Zamanlı'}
                                                </span>
                                            </div>
                                            {historyData.automation_info?.schedule_type && (
                                                <>
                                                    <div className="info-item">
                                                        <span className="label">Zamanlama:</span>
                                                        <span className="value">{historyData.automation_info.schedule_type}</span>
                                                    </div>
                                                    <div className="info-item">
                                                        <span className="label">Saat:</span>
                                                        <span className="value">{historyData.automation_info.schedule_value}</span>
                                                    </div>
                                                </>
                                            )}
                                            <div className="info-item">
                                                <span className="label">Durum:</span>
                                                <span className={`status-badge ${historyData.automation_info?.is_active ? 'active' : 'inactive'}`}>
                                                    {historyData.automation_info?.is_active ? '✅ Aktif' : '⏸️ Pasif'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="info-section">
                                        <h4>👤 Oluşturma Bilgileri</h4>
                                        <div className="info-grid">
                                            <div className="info-item">
                                                <span className="label">Oluşturan:</span>
                                                <span className="value">{historyData.automation_info?.created_by}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="label">Oluşturma Tarihi:</span>
                                                <span className="value">{formatDate(historyData.automation_info?.created_at, true)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {historyData.automation_info?.updated_at && historyData.automation_info?.updated_at !== historyData.automation_info?.created_at && (
                                        <div className="info-section">
                                            <h4>✏️ Son Güncelleme</h4>
                                            <div className="info-grid">
                                                <div className="info-item">
                                                    <span className="label">Güncelleyen:</span>
                                                    <span className="value">{historyData.automation_info?.updated_by}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">Güncelleme Tarihi:</span>
                                                    <span className="value">{formatDate(historyData.automation_info?.updated_at, true)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {historyData.automation_info?.last_run ? (
                                        <div className="info-section">
                                            <h4>⏰ Son Çalışma</h4>
                                            <div className="last-run-info">
                                                <span className="run-date">{formatDate(historyData.automation_info.last_run)}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="info-section">
                                            <h4>ℹ️ Çalışma Durumu</h4>
                                            <div className="last-run-info">
                                                <span className="run-date">Henüz çalışmadı</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="logs-content">
                                    {logsLoading ? (
                                        <div className="logs-loading">
                                            <div className="spinner"></div>
                                            <p>İşlem geçmişi yükleniyor...</p>
                                        </div>
                                    ) : activityLogs.length > 0 ? (
                                        <div className="logs-timeline">
                                            {activityLogs.map((log, index) => {
                                                const logStyle = getLogIcon(log);
                                                return (
                                                    <div key={log.id || index} className={`log-item ${log.type || ''}`}>
                                                        <div 
                                                            className="log-icon" 
                                                            style={{ 
                                                                backgroundColor: logStyle.color + '20', 
                                                                color: logStyle.color 
                                                            }}
                                                        >
                                                            <span>{logStyle.icon}</span>
                                                        </div>
                                                        <div className="log-content">
                                                            <div className="log-header">
                                                                <h4>{log.message}</h4>
                                                                <span className="log-time">{formatDate(log.created_at)}</span>
                                                            </div>
                                                            {log.details && (
                                                                <p className="log-details">{log.details}</p>
                                                            )}
                                                            {log.metadata && (
                                                                <div className="log-metadata">
                                                                    {log.metadata.channels_count && (
                                                                        <span className="meta-badge">
                                                                            📢 {log.metadata.channels_count} Kanal
                                                                        </span>
                                                                    )}
                                                                    {log.metadata.content_title && (
                                                                        <span className="meta-badge">
                                                                            📝 {log.metadata.content_title}
                                                                        </span>
                                                                    )}
                                                                    {log.metadata.tags && (
                                                                        <span className="meta-badge">
                                                                            🏷️ {log.metadata.tags}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {log.user && (
                                                                <div className="log-footer">
                                                                    <span className="log-user">👤 {log.user}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="empty-logs">
                                            <p>📭 Henüz işlem geçmişi bulunmuyor</p>
                                            <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                                                Otomasyon çalıştığında veya güncelleme yapıldığında burada görünecek.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button onClick={onClose} className="btn-close">
                                Kapat
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="error-state">
                        <p>❌ Bilgiler yüklenemedi</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AutomationHistoryModal;